/**
 * WB AI Chat Interface
 * Connects to RAG chat backend via REST (POST/GET) and WebSocket for streaming.
 */
import { ChatSessionManager } from './session';
import { getRateLimitPerHour, isCurrentlyRateLimited, tryConsumeRateLimitSlot, } from './rate-limit';
import { debugLog, renderMarkdown } from './utils';
const wbAiChatConfig = window.wbAiChatConfig;
/** Chat toggle button icon (sidebar mode). */
const TOGGLE_ICON_SVG = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C13.1 2 14 2.9 14 4V4.1C14 5.7 15.3 7 16.9 7H17C18.1 7 19 7.9 19 9V10.1C19 11.7 20.3 13 21.9 13H22V15H21.9C20.3 15 19 16.3 19 17.9V18C19 19.1 18.1 20 17 20H16.9C15.3 20 14 21.3 14 22.9V23H10V22.9C10 21.3 8.7 20 7.1 20H7C5.9 20 5 19.1 5 18V17.9C5 16.3 3.7 15 2.1 15H2V13H2.1C3.7 13 5 11.7 5 10.1V10C5 8.9 5.9 7 7 7H7.1C8.7 7 10 5.7 10 4.1V4C10 2.9 10.9 2 12 2Z" fill="white"/>
        <circle cx="9" cy="9" r="1" fill="white"/>
        <circle cx="15" cy="9" r="1" fill="white"/>
        <path d="M9 13C9 11.9 9.9 11 11 11H13C14.1 11 15 11.9 15 13V15H9V13Z" fill="white"/>
    </svg>
`;
/** localStorage key when the visitor dismisses the welcome bubble via its close control. */
const WB_AI_CHAT_WELCOME_DISMISS_STORAGE_KEY = 'wb_ai_chat_welcome_prompt_dismissed';
/** Delay after chat UI is ready before showing the welcome bubble (ms). */
const WB_AI_CHAT_WELCOME_SHOW_DELAY_MS = 3000;
/**
 * Filter REST conversation history for chat display.
 * Skips `tool` rows and empty `model` tool-call turns; maps non-empty `model` content to assistant.
 *
 * @param messages Raw history from GET /conversations/:id.
 * @returns Displayable user/assistant messages only.
 */
function filterHistoryForDisplay(messages) {
    const displayable = [];
    for (const m of messages) {
        if (m.role === 'user') {
            displayable.push({ role: 'user', content: m.content ?? '' });
        }
        else if (m.role === 'assistant') {
            displayable.push({ role: 'assistant', content: m.content ?? '' });
        }
        else if (m.role === 'model') {
            const text = (m.content ?? '').trim();
            if (text !== '') {
                displayable.push({ role: 'assistant', content: m.content ?? '' });
            }
        }
        // Skip tool rows and empty model tool-call turns
    }
    return displayable;
}
/**
 * Human-readable label for an optional `tool_call` activity indicator.
 *
 * @param toolName Backend tool name (e.g. lookup_knowledge_base).
 * @returns Short status label for the UI.
 */
function toolCallActivityLabel(toolName) {
    switch (toolName) {
        case 'lookup_knowledge_base':
            return 'Searching knowledge base…';
        case 'email_forward_enquiry':
            return 'Sending email…';
        default:
            return `Running ${toolName}…`;
    }
}
class WBAIChatInterface {
    constructor() {
        this.isInitialized = false;
        this.isOpen = false;
        this.eventListeners = {
            toggleClick: null,
            minimizeClick: null,
            closeClick: null,
            sendClick: null,
            stopClick: null,
            inputKeydown: null,
            inputInput: null,
            documentClick: null,
            welcomePromptDismissClick: null,
        };
        /** Wraps the floating toggle and optional welcome bubble (sidebar mode only). */
        this.toggleStack = null;
        this.toggleButton = null;
        this.welcomePromptBubble = null;
        /** Browser timer handle (number); avoid NodeJS.Timeout from ambient @types/node. */
        this.welcomePromptShowTimerId = null;
        /** Whether the full-page header resize listener has been attached (only once per page). */
        this.headerResizeBound = false;
        this.minimizeButton = null;
        this.closeButton = null;
        this.typingIndicator = null;
        this.ws = null;
        this.streamingBuffer = '';
        this.isStreaming = false;
        this.stopRequested = false;
        this.hasSavedConversationToWordPress = false;
        /** Current status phase from the server (`retrieval` / `generating` / `done` / `awaiting_ui`). */
        this.currentStatusPhase = null;
        /** Pending plugin UI pause; cleared on continuation, new question, or close. */
        this.pendingUi = null;
        this.sessionManager = new ChatSessionManager();
        this.isFullPage = wbAiChatConfig.isFullPage ?? false;
        this.init();
    }
    async init() {
        try {
            this.showLoadingState();
            const healthRes = await fetch(`${wbAiChatConfig.backendHttpUrl}/health`);
            if (!healthRes.ok) {
                throw new Error('Backend health check failed');
            }
            this.createChatInterface();
            this.bindEvents();
            // Lock sending if rate-limited, but still restore session / connect WS so history is visible.
            if (getRateLimitPerHour(wbAiChatConfig) > 0 && isCurrentlyRateLimited(wbAiChatConfig)) {
                this.applyRateLimitLock();
            }
            // No conversation is created here: sendMessageToBackend creates one on the first send,
            // so page views where the visitor never chats don't open backend sessions or sockets.
            if (this.sessionManager.loadSession()) {
                debugLog('Found cached conversation, attempting to restore...');
                if (await this.restoreConversation()) {
                    await this.openChat();
                    // Do not assume the conversation is already in WP — first send will
                    // upsert via saveConversationToWordPress (idempotent if the row exists).
                    debugLog('Conversation restored and chat opened');
                }
                else {
                    debugLog('Conversation could not be restored; a new one will be created on first send');
                }
            }
            this.isInitialized = true;
            this.hideLoadingState();
            if (!this.isFullPage) {
                const searchQuery = this.sessionManager.getSearchQueryFromURL();
                if (searchQuery) {
                    const prefixedQuery = `Help me find: ${searchQuery}`;
                    if (this.sessionManager.hasAlreadySentSearchQuery(prefixedQuery)) {
                        await this.openChat();
                    }
                    else {
                        await this.openChat();
                        setTimeout(() => this.sendSearchQuery(prefixedQuery), 500);
                    }
                }
            }
            this.scheduleWelcomePromptBubble();
            debugLog('WB AI Chat initialized successfully');
        }
        catch (error) {
            console.error('Failed to initialize WB AI Chat:', error);
            this.hideLoadingState();
            this.showToggleError();
        }
    }
    createChatInterface() {
        const fp = this.isFullPage;
        if (!fp && this.container?.isConnected) {
            this.clearWelcomePromptShowTimer();
            this.unBindEvents();
            this.container.remove();
            this.toggleButton = null;
            this.toggleStack = null;
            this.sidebar = undefined;
            this.welcomePromptBubble = null;
        }
        else if (!fp && this.toggleButton?.parentNode === document.body) {
            this.toggleButton.remove();
            this.toggleButton = null;
        }
        if (fp) {
            this.setHeaderHeight();
        }
        this.container = document.createElement('div');
        this.container.id = fp ? 'wb-ai-chat-full-page' : 'wb-ai-chat-container';
        this.container.className = fp ? 'wb-ai-chat-full-page' : 'wb-ai-chat-container';
        if (!fp) {
            this.toggleStack = document.createElement('div');
            this.toggleStack.className = 'wb-ai-chat-toggle-stack';
            this.toggleButton = document.createElement('button');
            this.toggleButton.id = 'wb-ai-chat-toggle';
            this.toggleButton.className = 'wb-ai-chat-toggle';
            this.toggleButton.setAttribute('aria-label', 'Open AI Chat');
            this.toggleButton.setAttribute('aria-expanded', 'false');
            this.toggleButton.setAttribute('aria-controls', 'wb-ai-chat-sidebar');
            this.toggleButton.innerHTML = TOGGLE_ICON_SVG;
            this.toggleStack.appendChild(this.toggleButton);
            this.sidebar = document.createElement('div');
            this.sidebar.id = 'wb-ai-chat-sidebar';
            this.sidebar.className = 'wb-ai-chat-sidebar';
        }
        this.chatHeader = document.createElement('div');
        this.chatHeader.className = fp ? 'wb-ai-chat-full-page-header' : 'wb-ai-chat-header';
        const closeButton = `
        <button class="wb-ai-chat-close" aria-label="Close chat">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>
        `;
        this.chatHeader.innerHTML = fp
            ? `
            <h2></h2>
            ${closeButton}
        `
            : `
            <h3></h3>
            <div class="wb-ai-chat-header-buttons">
                <button class="wb-ai-chat-minimize" aria-label="Minimize chat">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12L18 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
                ${closeButton}
            </div>
        `;
        this.chatHeader.querySelector('h2, h3').textContent = wbAiChatConfig.title;
        this.messagesContainer = document.createElement('div');
        this.messagesContainer.className = 'wb-ai-chat-messages' + (fp ? ' wb-ai-chat-full-page-messages' : '');
        this.messagesContainer.setAttribute('role', 'log');
        this.messagesContainer.setAttribute('aria-label', 'Chat messages');
        this.inputContainer = document.createElement('div');
        this.inputContainer.className = 'wb-ai-chat-input-container' + (fp ? ' wb-ai-chat-full-page-input-container' : '');
        const sendSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        const stopSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/></svg>`;
        this.input = document.createElement('textarea');
        this.input.className = 'wb-ai-chat-input' + (fp ? ' wb-ai-chat-full-page-input' : '');
        this.input.placeholder = 'Type your message...';
        this.input.setAttribute('aria-label', 'Message');
        this.input.setAttribute('rows', '1');
        this.input.setAttribute('maxlength', '2000');
        this.sendButton = document.createElement('button');
        this.sendButton.className = 'wb-ai-chat-send' + (fp ? ' wb-ai-chat-full-page-send' : '');
        this.sendButton.setAttribute('aria-label', 'Send message');
        this.sendButton.innerHTML = sendSvg;
        this.stopButton = document.createElement('button');
        this.stopButton.className = 'wb-ai-chat-stop' + (fp ? ' wb-ai-chat-full-page-stop' : '');
        this.stopButton.setAttribute('aria-label', 'Stop message generation');
        this.stopButton.innerHTML = stopSvg;
        this.stopButton.style.display = 'none';
        this.inputContainer.appendChild(this.input);
        this.inputContainer.appendChild(this.sendButton);
        this.inputContainer.appendChild(this.stopButton);
        const contentRoot = fp ? this.container : this.sidebar;
        contentRoot.appendChild(this.chatHeader);
        contentRoot.appendChild(this.messagesContainer);
        contentRoot.appendChild(this.inputContainer);
        if (!fp) {
            this.container.appendChild(this.toggleStack);
            this.container.appendChild(this.sidebar);
        }
        if (fp) {
            const shortcodeContainer = document.getElementById('wb-ai-chat-full-page__container');
            if (shortcodeContainer) {
                shortcodeContainer.appendChild(this.container);
            }
            else {
                document.body.appendChild(this.container);
            }
            setTimeout(() => this.input.focus(), 100);
        }
        else {
            document.body.appendChild(this.container);
        }
    }
    /**
     * Load an existing conversation from the backend and render displayable history.
     * Returns false (and clears the cached session) when the conversation is missing,
     * unreachable, or has no displayable messages — caller should create a new one.
     *
     * @returns True when history was rendered and the WebSocket was connected.
     */
    async restoreConversation() {
        const conversationId = this.sessionManager.getCurrentConversationId();
        if (!conversationId)
            return false;
        try {
            const res = await fetch(`${wbAiChatConfig.backendHttpUrl}/conversations/${encodeURIComponent(conversationId)}`);
            if (!res.ok) {
                this.sessionManager.clearSession();
                return false;
            }
            const messages = await res.json();
            const displayable = filterHistoryForDisplay(messages);
            if (displayable.length === 0) {
                // Empty / non-displayable history → treat as unrestorable so we start fresh.
                this.sessionManager.clearSession();
                return false;
            }
            this.clearMessages();
            displayable.forEach((m) => this.addMessage(m.content, m.role));
            this.connectWebSocket();
            return true;
        }
        catch (e) {
            console.error('Restore conversation failed:', e);
            this.sessionManager.clearSession();
            return false;
        }
    }
    /**
     * Create a new conversation via POST /conversations (no body), then establish WebSocket.
     * Called on init when there is no existing session to restore, or restore failed.
     * Resets the WP save flag so the first user message persists this new session.
     */
    async createConversationAndConnectWs() {
        const res = await fetch(`${wbAiChatConfig.backendHttpUrl}/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
            const errBody = await res.json().catch(() => ({}));
            throw new Error(errBody.error ?? `HTTP ${res.status}`);
        }
        const body = await res.json();
        this.sessionManager.setCurrentConversationId(body.conversation_id);
        this.sessionManager.saveSession();
        // New conversation must be saved to WP on first send, even if a prior save failed.
        this.hasSavedConversationToWordPress = false;
        const wsUrl = body.websocket_endpoint ??
            `${wbAiChatConfig.backendWsUrl}/ws/${encodeURIComponent(body.conversation_id)}`;
        this.attachWebSocket(new WebSocket(wsUrl));
    }
    /**
     * Bind handlers on a WebSocket and store it as the active connection.
     * `onclose` only clears `this.ws` when it still refers to this socket.
     *
     * @param socket Newly created WebSocket.
     */
    attachWebSocket(socket) {
        this.ws = socket;
        socket.onopen = () => {
            debugLog('WebSocket connected');
        };
        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.handleWsMessage(data);
            }
            catch (e) {
                console.error('Invalid WebSocket message', e);
            }
        };
        socket.onerror = () => {
            console.error('WebSocket error');
        };
        socket.onclose = () => {
            if (this.ws === socket) {
                this.ws = null;
            }
        };
    }
    setHeaderHeight() {
        const headerElement = document.querySelector('.wp-block-template-part');
        if (headerElement) {
            const apply = () => {
                const height = headerElement.offsetHeight + 'px';
                document.documentElement.style.setProperty('--header-height', height);
                document.documentElement.style.setProperty('--header-height-mobile', height);
            };
            apply();
            if (!this.headerResizeBound) {
                this.headerResizeBound = true;
                window.addEventListener('resize', apply);
            }
        }
        else {
            document.documentElement.style.setProperty('--header-height', '60px');
            document.documentElement.style.setProperty('--header-height-mobile', '50px');
        }
    }
    unBindEvents() {
        if (this.toggleButton && this.eventListeners.toggleClick) {
            this.toggleButton.removeEventListener('click', this.eventListeners.toggleClick);
            this.eventListeners.toggleClick = null;
        }
        if (this.minimizeButton && this.eventListeners.minimizeClick) {
            this.minimizeButton.removeEventListener('click', this.eventListeners.minimizeClick);
            this.eventListeners.minimizeClick = null;
        }
        if (this.closeButton && this.eventListeners.closeClick) {
            this.closeButton.removeEventListener('click', this.eventListeners.closeClick);
            this.eventListeners.closeClick = null;
        }
        if (this.sendButton && this.eventListeners.sendClick) {
            this.sendButton.removeEventListener('click', this.eventListeners.sendClick);
            this.eventListeners.sendClick = null;
        }
        if (this.stopButton && this.eventListeners.stopClick) {
            this.stopButton.removeEventListener('click', this.eventListeners.stopClick);
            this.eventListeners.stopClick = null;
        }
        if (this.input && this.eventListeners.inputKeydown) {
            this.input.removeEventListener('keydown', this.eventListeners.inputKeydown);
            this.eventListeners.inputKeydown = null;
        }
        if (this.input && this.eventListeners.inputInput) {
            this.input.removeEventListener('input', this.eventListeners.inputInput);
            this.eventListeners.inputInput = null;
        }
        if (this.eventListeners.documentClick) {
            document.removeEventListener('click', this.eventListeners.documentClick);
            this.eventListeners.documentClick = null;
        }
        this.detachWelcomePromptDismissListener();
    }
    bindEvents() {
        if (this.toggleButton) {
            this.eventListeners.toggleClick = () => this.toggleChat();
            this.toggleButton.addEventListener('click', this.eventListeners.toggleClick);
        }
        this.minimizeButton = this.chatHeader?.querySelector('.wb-ai-chat-minimize') ?? null;
        if (this.minimizeButton) {
            this.eventListeners.minimizeClick = () => this.minimizeChat();
            this.minimizeButton.addEventListener('click', this.eventListeners.minimizeClick);
        }
        this.closeButton = this.chatHeader?.querySelector('.wb-ai-chat-close, .wb-ai-chat-full-page-close') ?? null;
        if (this.closeButton) {
            this.eventListeners.closeClick = () => this.closeChat();
            this.closeButton.addEventListener('click', this.eventListeners.closeClick);
        }
        this.eventListeners.sendClick = () => this.sendMessage();
        this.sendButton.addEventListener('click', this.eventListeners.sendClick);
        this.eventListeners.stopClick = () => this.stopMessageGeneration();
        this.stopButton.addEventListener('click', this.eventListeners.stopClick);
        this.eventListeners.inputKeydown = (e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !this.isStreaming) {
                e.preventDefault();
                this.sendMessage();
            }
            else if (e.key === 'Escape' && !this.isFullPage && this.isOpen) {
                this.minimizeChat();
                this.toggleButton?.focus();
            }
        };
        this.input.addEventListener('keydown', this.eventListeners.inputKeydown);
        this.eventListeners.inputInput = () => this.autoResizeTextarea();
        this.input.addEventListener('input', this.eventListeners.inputInput);
        if (!this.isFullPage) {
            this.eventListeners.documentClick = (e) => {
                if (e.target && !this.container.contains(e.target) && this.isOpen) {
                    this.minimizeChat();
                }
            };
            document.addEventListener('click', this.eventListeners.documentClick);
        }
        // Ensure send button is shown initially
        this.showSendButton();
        this.attachWelcomePromptDismissListenerIfNeeded();
    }
    /**
     * True when hourly send cap is enabled and the browser is still in a blocked window.
     */
    isClientRateLimited() {
        return getRateLimitPerHour(wbAiChatConfig) > 0 && isCurrentlyRateLimited(wbAiChatConfig);
    }
    /**
     * Re-enable the input once the rolling window / localStorage expiry allows sends again.
     */
    unlockInputIfRateLimitExpired() {
        if (this.isStreaming) {
            return;
        }
        if (getRateLimitPerHour(wbAiChatConfig) <= 0) {
            return;
        }
        if (!isCurrentlyRateLimited(wbAiChatConfig)) {
            this.setInputDisabled(false);
        }
    }
    /**
     * Show the rate-limit message and disable sending until the limit window resets.
     */
    applyRateLimitLock() {
        this.showRateLimitMessage();
        this.setInputDisabled(true);
    }
    /**
     * Surface the configured rate-limit copy in the message list.
     */
    showRateLimitMessage() {
        if (this.messagesContainer.querySelector('.wb-ai-chat-rate-limit-notice')) {
            return;
        }
        const msg = wbAiChatConfig.strings.rateLimitReached ??
            "You've reached your chat limit for now. Please try again later.";
        const errorElement = document.createElement('div');
        errorElement.className =
            'wb-ai-chat-message wb-ai-chat-message-error wb-ai-chat-rate-limit-notice';
        const contentElement = document.createElement('div');
        contentElement.className = 'wb-ai-chat-message-content';
        contentElement.textContent = msg;
        errorElement.appendChild(contentElement);
        this.messagesContainer.appendChild(errorElement);
        this.scrollToLastMessageTop();
    }
    async openChat() {
        this.dismissWelcomePromptForChatOpen();
        this.unlockInputIfRateLimitExpired();
        this.isOpen = true;
        this.container.classList.add('open');
        this.updateToggleState();
        this.input.focus();
        if (this.isClientRateLimited()) {
            this.showRateLimitMessage();
            this.setInputDisabled(true);
        }
        setTimeout(() => {
            this.scrollToBottom();
        }, 300);
    }
    minimizeChat() {
        this.isOpen = false;
        this.container.classList.remove('open');
        this.updateToggleState();
    }
    /**
     * Sync the toggle button's accessible label and expanded state with `isOpen`.
     */
    updateToggleState() {
        if (this.toggleButton) {
            this.toggleButton.setAttribute('aria-label', this.isOpen ? 'Close AI Chat' : 'Open AI Chat');
            this.toggleButton.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
        }
    }
    toggleChat() {
        if (this.isOpen) {
            this.minimizeChat();
        }
        else {
            this.openChat().catch((e) => console.error('Failed to open chat:', e));
        }
    }
    closeChat() {
        this.stopMessageGeneration();
        this.clearPendingUi();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.isOpen = false;
        this.container.classList.remove('open');
        this.updateToggleState();
        this.sessionManager.clearSession();
        this.hasSavedConversationToWordPress = false;
        this.clearMessages();
        this.input.value = '';
        this.autoResizeTextarea();
        this.hideTypingIndicator();
        this.currentStatusPhase = null;
        debugLog('Chat history cleared, ready for new conversation');
        this.unBindEvents();
        this.bindEvents();
    }
    clearMessages() {
        const existingMessages = this.messagesContainer.querySelectorAll('.wb-ai-chat-message');
        existingMessages.forEach((msg) => msg.remove());
    }
    stopMessageGeneration() {
        if (!this.isStreaming)
            return;
        this.stopRequested = true;
        this.isStreaming = false;
        this.currentStatusPhase = null;
        this.hideTypingIndicator();
        this.setInputDisabled(false);
        this.showSendButton();
    }
    /**
     * Prefill the input with a search-derived query and send it.
     * Only records the query as sent when the message actually goes out.
     *
     * @param query Prefixed search query text.
     */
    async sendSearchQuery(query) {
        this.input.value = query;
        this.autoResizeTextarea();
        const sent = await this.sendMessage();
        if (sent) {
            this.sessionManager.saveSentSearchQuery(query);
        }
    }
    /**
     * Send the current input as a user message.
     *
     * @returns True when the message was accepted and handed to the backend; false if blocked or failed before send.
     */
    async sendMessage() {
        this.unlockInputIfRateLimitExpired();
        const message = this.input.value.trim();
        if (!message || !this.isInitialized) {
            return false;
        }
        if (getRateLimitPerHour(wbAiChatConfig) > 0) {
            if (!tryConsumeRateLimitSlot(wbAiChatConfig)) {
                this.showRateLimitMessage();
                this.setInputDisabled(true);
                return false;
            }
        }
        // New question cancels any pending UI pause server-side
        this.clearPendingUi();
        this.setInputDisabled(true);
        this.showStopButton();
        this.input.value = '';
        this.autoResizeTextarea();
        this.addMessage(message, 'user');
        this.showTypingIndicator();
        try {
            await this.sendMessageToBackend(message);
            return true;
        }
        catch (error) {
            console.error('Error sending message:', error);
            this.isStreaming = false;
            this.currentStatusPhase = null;
            this.showSendButton();
            this.hideTypingIndicator();
            let errorMessage = wbAiChatConfig.strings.error;
            const errMsg = error instanceof Error ? error.message : String(error);
            if (errMsg.includes('network') || errMsg.includes('fetch')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            }
            else if (errMsg.includes('API')) {
                errorMessage = 'AI service temporarily unavailable. Please try again later.';
            }
            this.showError(errorMessage);
            this.setInputDisabled(false);
            return false;
        }
    }
    addMessage(content, sender) {
        const messageElement = document.createElement('div');
        messageElement.className = `wb-ai-chat-message wb-ai-chat-message-${sender}`;
        const contentElement = document.createElement('div');
        contentElement.className = 'wb-ai-chat-message-content';
        if (sender === 'assistant' && content) {
            contentElement.innerHTML = renderMarkdown(content);
        }
        else {
            contentElement.textContent = content;
        }
        messageElement.appendChild(contentElement);
        this.messagesContainer.appendChild(messageElement);
        this.scrollToLastMessageTop();
    }
    appendToLastAssistantContent(html) {
        const last = this.messagesContainer.querySelector('.wb-ai-chat-message-assistant:last-child .wb-ai-chat-message-content');
        if (last)
            last.innerHTML = html;
    }
    /**
     * Show a status message (e.g. "Looking through the knowledge base...") in the last assistant
     * message using .wb-ai-chat-partial-content. Used until the first content chunk arrives.
     *
     * @param message Status label from the server (or a local tool-activity label).
     */
    setLastAssistantStatusMessage(message) {
        const contentEl = this.messagesContainer.querySelector('.wb-ai-chat-message-assistant:last-child .wb-ai-chat-message-content');
        if (!contentEl)
            return;
        let partial = contentEl.querySelector('.wb-ai-chat-partial-content');
        if (!partial) {
            partial = document.createElement('div');
            partial.className = 'wb-ai-chat-partial-content';
            contentEl.appendChild(partial);
        }
        partial.textContent = message;
    }
    /**
     * Apply a server `status` phase to local UI state.
     * Prefer `phase` for control flow; show `message` as the label while waiting for chunks.
     *
     * @param phase Status phase from the server.
     * @param message Human-readable status label.
     */
    applyStatusPhase(phase, message) {
        if (phase) {
            this.currentStatusPhase = phase;
        }
        if (phase === 'done') {
            this.finishTurnUi();
            return;
        }
        if (phase === 'awaiting_ui') {
            this.hideTypingIndicator();
            if (this.isStreaming && this.streamingBuffer === '' && message) {
                this.setLastAssistantStatusMessage(message);
            }
            return;
        }
        // retrieval / generating / unknown: show label until content arrives
        if (this.isStreaming && this.streamingBuffer === '' && message) {
            this.setLastAssistantStatusMessage(message);
        }
    }
    /**
     * End the current turn's input/streaming chrome.
     * Not used for UI pause — that path keeps the input usable after `stream_end`.
     */
    finishTurnUi() {
        this.isStreaming = false;
        this.currentStatusPhase = null;
        this.hideTypingIndicator();
        this.setInputDisabled(false);
        this.showSendButton();
    }
    /**
     * Remove the last assistant bubble if it has no visible content (e.g. after an early error).
     */
    removeEmptyLastAssistantMessage() {
        const last = this.messagesContainer.querySelector('.wb-ai-chat-message-assistant:last-child');
        if (!last || last.classList.contains('wb-ai-chat-typing')) {
            return;
        }
        const contentEl = last.querySelector('.wb-ai-chat-message-content');
        if (!contentEl) {
            return;
        }
        const text = (contentEl.textContent ?? '').trim();
        const hasPartialOnly = contentEl.querySelector('.wb-ai-chat-partial-content') !== null &&
            !contentEl.querySelector('.wb-ai-chat-paragraph, .wb-ai-chat-heading, .wb-ai-chat-list, a, strong');
        if (text === '' || hasPartialOnly) {
            last.remove();
        }
    }
    /**
     * Clear pending plugin UI pause state and remove any UI panel from the DOM.
     */
    clearPendingUi() {
        this.pendingUi = null;
        this.messagesContainer
            .querySelectorAll('.wb-ai-chat-ui-panel')
            .forEach((el) => el.remove());
        if (this.currentStatusPhase === 'awaiting_ui') {
            this.currentStatusPhase = null;
        }
    }
    /**
     * Render a plugin UI pause panel and store pending state for `tool_continuation`.
     *
     * @param data Server `ui` message.
     */
    handleUiPause(data) {
        this.pendingUi = data;
        this.currentStatusPhase = 'awaiting_ui';
        this.hideTypingIndicator();
        // Remove any previous panel for this turn
        this.messagesContainer
            .querySelectorAll('.wb-ai-chat-ui-panel')
            .forEach((el) => el.remove());
        const panel = document.createElement('div');
        panel.className = 'wb-ai-chat-message wb-ai-chat-ui-panel';
        panel.dataset.toolCallId = data.tool_call_id;
        panel.dataset.action = data.action;
        const content = document.createElement('div');
        content.className = 'wb-ai-chat-message-content wb-ai-chat-ui-panel-content';
        const title = document.createElement('div');
        title.className = 'wb-ai-chat-ui-panel-title';
        title.textContent = this.uiActionTitle(data.action);
        const body = document.createElement('div');
        body.className = 'wb-ai-chat-ui-panel-body';
        if (data.action === 'stripe_payment') {
            body.appendChild(this.buildStripePaymentUi(data, panel));
        }
        else {
            body.appendChild(this.buildGenericUiForm(data, panel));
        }
        content.appendChild(title);
        content.appendChild(body);
        panel.appendChild(content);
        this.messagesContainer.appendChild(panel);
        this.scrollToLastMessageTop();
    }
    /**
     * Title for a plugin UI action.
     *
     * @param action Plugin UI action id.
     * @returns Display title.
     */
    uiActionTitle(action) {
        switch (action) {
            case 'stripe_payment':
                return 'Complete payment';
            default:
                return 'Additional step required';
        }
    }
    /**
     * Build Stripe payment UI (placeholder until Stripe.js is wired).
     * Continues with a cancelled/succeeded response once the user acts.
     *
     * @param data UI message with payload (e.g. client_secret).
     * @param panel Panel element to disable after submit.
     * @returns Form root element.
     */
    buildStripePaymentUi(data, panel) {
        const wrap = document.createElement('div');
        wrap.className = 'wb-ai-chat-ui-stripe';
        const note = document.createElement('p');
        note.className = 'wb-ai-chat-ui-panel-note';
        note.textContent =
            'A payment step is required to continue. Complete payment in your payment form, then confirm below.';
        const secret = data.payload?.client_secret;
        if (typeof secret === 'string' && secret !== '') {
            const meta = document.createElement('p');
            meta.className = 'wb-ai-chat-ui-panel-meta';
            meta.textContent = 'Payment session ready.';
            wrap.appendChild(note);
            wrap.appendChild(meta);
        }
        else {
            wrap.appendChild(note);
        }
        const actions = document.createElement('div');
        actions.className = 'wb-ai-chat-ui-panel-actions';
        const succeedBtn = document.createElement('button');
        succeedBtn.type = 'button';
        succeedBtn.className = 'wb-ai-chat-ui-continue';
        succeedBtn.textContent = 'Payment succeeded';
        succeedBtn.addEventListener('click', () => {
            const paymentIntentId = typeof data.payload?.payment_intent_id === 'string'
                ? data.payload.payment_intent_id
                : typeof secret === 'string'
                    ? secret.split('_secret_')[0] ?? ''
                    : '';
            this.sendToolContinuation(data.tool_call_id, {
                payment_status: 'succeeded',
                payment_intent_id: paymentIntentId,
            }, panel);
        });
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'wb-ai-chat-ui-cancel';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => {
            this.sendToolContinuation(data.tool_call_id, {
                payment_status: 'cancelled',
            }, panel);
        });
        actions.appendChild(succeedBtn);
        actions.appendChild(cancelBtn);
        wrap.appendChild(actions);
        return wrap;
    }
    /**
     * Generic JSON-ish continuation form for unknown UI actions.
     *
     * @param data UI message.
     * @param panel Panel element to disable after submit.
     * @returns Form root element.
     */
    buildGenericUiForm(data, panel) {
        const wrap = document.createElement('div');
        wrap.className = 'wb-ai-chat-ui-generic';
        const note = document.createElement('p');
        note.className = 'wb-ai-chat-ui-panel-note';
        note.textContent = `Action “${data.action}” requires a response to continue.`;
        const textarea = document.createElement('textarea');
        textarea.className = 'wb-ai-chat-ui-json-input';
        textarea.rows = 3;
        textarea.placeholder = '{"status":"ok"}';
        textarea.value = JSON.stringify(data.payload ?? { status: 'ok' }, null, 2);
        const actions = document.createElement('div');
        actions.className = 'wb-ai-chat-ui-panel-actions';
        const continueBtn = document.createElement('button');
        continueBtn.type = 'button';
        continueBtn.className = 'wb-ai-chat-ui-continue';
        continueBtn.textContent = 'Continue';
        continueBtn.addEventListener('click', () => {
            let response;
            try {
                const parsed = JSON.parse(textarea.value);
                if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                    this.showError('Continuation response must be a JSON object.');
                    return;
                }
                response = parsed;
            }
            catch {
                this.showError('Invalid JSON in continuation response.');
                return;
            }
            this.sendToolContinuation(data.tool_call_id, response, panel);
        });
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'wb-ai-chat-ui-cancel';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => {
            this.sendToolContinuation(data.tool_call_id, { status: 'cancelled' }, panel);
        });
        actions.appendChild(continueBtn);
        actions.appendChild(cancelBtn);
        wrap.appendChild(note);
        wrap.appendChild(textarea);
        wrap.appendChild(actions);
        return wrap;
    }
    /**
     * Send `tool_continuation` over the WebSocket and prepare for the continuation stream.
     *
     * @param toolCallId Must match the pending `ui` message.
     * @param response Plugin-defined JSON object.
     * @param panel Optional panel to mark as submitted.
     */
    sendToolContinuation(toolCallId, response, panel) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            this.showError('Connection lost. Please try again.');
            return;
        }
        if (panel) {
            panel.classList.add('wb-ai-chat-ui-panel--submitted');
            panel.querySelectorAll('button, textarea').forEach((el) => {
                el.disabled = true;
            });
        }
        this.pendingUi = null;
        this.currentStatusPhase = null;
        this.setInputDisabled(true);
        this.showStopButton();
        this.showTypingIndicator();
        const continuation = {
            type: 'tool_continuation',
            tool_call_id: toolCallId,
            response,
        };
        this.ws.send(JSON.stringify(continuation));
    }
    /**
     * Persist the conversation in wb_ai_chat_conversations (idempotent if the row exists).
     * Only marks the session as saved after a successful response so a failed save is retried
     * on the next user message.
     *
     * @param conversationId Backend conversation id.
     * @param firstMessage First user message (shown in admin review list).
     */
    saveConversationToWordPress(conversationId, firstMessage) {
        const url = wbAiChatConfig.saveConversationUrl;
        const nonce = wbAiChatConfig.saveConversationNonce;
        if (!url || !nonce)
            return;
        const headers = {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
        };
        fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ conversation_id: conversationId, first_message: firstMessage }),
            credentials: 'same-origin',
        })
            .then((res) => {
            if (res.ok) {
                this.hasSavedConversationToWordPress = true;
            }
        })
            .catch(() => {
            // Leave hasSavedConversationToWordPress false so the next send retries.
        });
    }
    connectWebSocket() {
        const conversationId = this.sessionManager.getCurrentConversationId();
        if (!conversationId)
            return;
        const wsUrl = `${wbAiChatConfig.backendWsUrl}/ws/${encodeURIComponent(conversationId)}`;
        this.attachWebSocket(new WebSocket(wsUrl));
    }
    /**
     * Ensure `this.ws` is OPEN. Reuses an in-progress CONNECTING socket instead of replacing it.
     *
     * @param timeoutMs Max wait for open (default 10s).
     */
    async ensureWebSocketOpen(timeoutMs = 10000) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }
        // Only create a new socket when missing or already closed; never abort a CONNECTING one.
        if (!this.ws ||
            this.ws.readyState === WebSocket.CLOSED ||
            this.ws.readyState === WebSocket.CLOSING) {
            this.connectWebSocket();
        }
        await this.waitForWebSocketOpen(timeoutMs);
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket not open');
        }
    }
    handleWsMessage(data) {
        switch (data.type) {
            case 'history':
                if (this.typingIndicator) {
                    return;
                }
                {
                    const displayable = filterHistoryForDisplay(data.messages ?? []);
                    // Avoid wiping REST-restored messages when the WS history payload is empty.
                    if (displayable.length === 0 &&
                        this.messagesContainer.querySelector('.wb-ai-chat-message')) {
                        break;
                    }
                    this.clearMessages();
                    this.clearPendingUi();
                    displayable.forEach((m) => this.addMessage(m.content, m.role));
                }
                this.isStreaming = false;
                this.currentStatusPhase = null;
                this.hideTypingIndicator();
                this.setInputDisabled(false);
                this.showSendButton();
                break;
            case 'message':
                this.isStreaming = false;
                this.currentStatusPhase = null;
                this.hideTypingIndicator();
                this.addMessage(data.content ?? '', 'assistant');
                this.setInputDisabled(false);
                this.showSendButton();
                break;
            case 'stream_start':
                this.streamingBuffer = '';
                this.isStreaming = true;
                this.stopRequested = false;
                this.currentStatusPhase = null;
                this.hideTypingIndicator();
                this.addMessage('', 'assistant');
                break;
            case 'status':
                this.applyStatusPhase(data.phase, data.message ?? '');
                break;
            case 'tool_call':
                if (this.isStreaming && this.streamingBuffer === '' && data.name) {
                    this.setLastAssistantStatusMessage(toolCallActivityLabel(data.name));
                }
                break;
            case 'chunk':
                if (this.stopRequested)
                    break;
                if (typeof data.content === 'string') {
                    this.streamingBuffer += data.content;
                    // Re-render the full message so markdown split across chunks (e.g. links, headings) renders correctly
                    this.appendToLastAssistantContent(renderMarkdown(this.streamingBuffer));
                }
                break;
            case 'ui':
                this.handleUiPause(data);
                break;
            case 'stream_end':
                // The user already stopped this turn and the UI was reset then. Ignoring the late
                // stream_end keeps it from clobbering a follow-up question that is now pending.
                if (this.stopRequested)
                    break;
                // UI pause and errors are valid endings without phase:done
                if (this.currentStatusPhase === 'awaiting_ui' || this.pendingUi) {
                    this.isStreaming = false;
                    this.hideTypingIndicator();
                    this.setInputDisabled(false);
                    this.showSendButton();
                    break;
                }
                this.finishTurnUi();
                break;
            case 'full_message':
                // Like chunks, the final text of a stopped turn must not be rendered.
                if (this.stopRequested)
                    break;
                if (typeof data.content === 'string' && data.content !== '') {
                    this.streamingBuffer = data.content;
                    this.appendToLastAssistantContent(renderMarkdown(data.content));
                }
                break;
            case 'error':
                // End the turn immediately; do not wait for phase:done
                this.isStreaming = false;
                this.currentStatusPhase = null;
                this.hideTypingIndicator();
                this.removeEmptyLastAssistantMessage();
                this.setInputDisabled(false);
                this.showSendButton();
                this.showError(data.error ?? wbAiChatConfig.strings.error);
                break;
        }
    }
    /**
     * Wait until the current WebSocket is OPEN, or reject on timeout / close / error.
     *
     * @param timeoutMs Max wait before rejecting (default 10s).
     */
    waitForWebSocketOpen(timeoutMs = 10000) {
        return new Promise((resolve, reject) => {
            const ws = this.ws;
            if (!ws) {
                reject(new Error('WebSocket not created'));
                return;
            }
            if (ws.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }
            if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                reject(new Error('WebSocket closed before open'));
                return;
            }
            let settled = false;
            const cleanup = () => {
                window.clearTimeout(timerId);
                ws.removeEventListener('open', onOpen);
                ws.removeEventListener('error', onError);
                ws.removeEventListener('close', onClose);
            };
            const finish = (fn) => {
                if (settled)
                    return;
                settled = true;
                cleanup();
                fn();
            };
            const onOpen = () => finish(() => resolve());
            const onError = () => finish(() => reject(new Error('WebSocket connection error')));
            const onClose = () => finish(() => reject(new Error('WebSocket closed before open')));
            const timerId = window.setTimeout(() => {
                finish(() => reject(new Error('WebSocket open timeout')));
            }, timeoutMs);
            ws.addEventListener('open', onOpen);
            ws.addEventListener('error', onError);
            ws.addEventListener('close', onClose);
            // open may have fired between the initial readyState check and listener attach
            if (ws.readyState === WebSocket.OPEN) {
                finish(() => resolve());
            }
            else if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                finish(() => reject(new Error('WebSocket closed before open')));
            }
        });
    }
    /**
     * Send a message to the backend. All messages go over the WebSocket.
     * Ensures we have a conversation_id and an open WebSocket before sending.
     */
    async sendMessageToBackend(message, retryCount = 0) {
        const maxRetries = 2;
        try {
            let conversationId = this.sessionManager.getCurrentConversationId();
            if (!conversationId) {
                await this.createConversationAndConnectWs();
                conversationId = this.sessionManager.getCurrentConversationId();
            }
            if (!conversationId) {
                throw new Error('No conversation ID after create');
            }
            await this.ensureWebSocketOpen();
            this.ws.send(JSON.stringify({ question: message }));
            // Persist to WP after the question is on the wire. Flag is set only on success
            // so a failed save (or a restored session never registered in WP) is retried.
            if (!this.hasSavedConversationToWordPress) {
                this.saveConversationToWordPress(conversationId, message);
            }
        }
        catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error);
            if (retryCount < maxRetries &&
                (errMsg.includes('network') ||
                    errMsg.includes('fetch') ||
                    errMsg.includes('Failed to fetch') ||
                    errMsg.includes('WebSocket'))) {
                await new Promise((r) => setTimeout(r, 1000 * (retryCount + 1)));
                return this.sendMessageToBackend(message, retryCount + 1);
            }
            throw error;
        }
    }
    createTypingIndicator() {
        const typingElement = document.createElement('div');
        typingElement.className = 'wb-ai-chat-typing-indicator';
        typingElement.innerHTML = `
            <span></span>
            <span></span>
            <span></span>
        `;
        return typingElement;
    }
    showTypingIndicator() {
        if (this.typingIndicator) {
            return;
        }
        this.typingIndicator = document.createElement('div');
        this.typingIndicator.className = 'wb-ai-chat-message wb-ai-chat-message-assistant wb-ai-chat-typing';
        const contentElement = document.createElement('div');
        contentElement.className = 'wb-ai-chat-message-content';
        contentElement.appendChild(this.createTypingIndicator());
        this.typingIndicator.appendChild(contentElement);
        this.messagesContainer.appendChild(this.typingIndicator);
        this.scrollToLastMessageTop();
    }
    hideTypingIndicator() {
        if (this.typingIndicator) {
            this.typingIndicator.remove();
            this.typingIndicator = null;
        }
    }
    showError(message) {
        const errorElement = document.createElement('div');
        errorElement.className = 'wb-ai-chat-message wb-ai-chat-message-error';
        const contentElement = document.createElement('div');
        contentElement.className = 'wb-ai-chat-message-content';
        contentElement.textContent = message;
        errorElement.appendChild(contentElement);
        this.messagesContainer.appendChild(errorElement);
        this.scrollToLastMessageTop();
    }
    autoResizeTextarea() {
        this.input.style.height = 'auto';
        this.input.style.height = Math.min(this.input.scrollHeight + 16, 120) + 'px';
    }
    scrollToBottom() {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }
    scrollToLastMessageTop() {
        const lastMessage = this.messagesContainer.lastElementChild;
        if (lastMessage) {
            const headerHeight = this.chatHeader.offsetHeight;
            const scrollTo = Math.max(0, lastMessage.offsetTop - headerHeight - 5);
            this.messagesContainer.scrollTop = scrollTo;
        }
        else {
            this.scrollToBottom();
        }
    }
    /**
     * Sidebar only: after the UI is ready, wait then show the optional welcome bubble above the toggle.
     */
    scheduleWelcomePromptBubble() {
        if (this.isFullPage) {
            return;
        }
        const prompt = wbAiChatConfig.welcomePrompt;
        if (!prompt || typeof prompt !== 'string') {
            return;
        }
        this.clearWelcomePromptShowTimer();
        this.welcomePromptShowTimerId = window.setTimeout(() => {
            this.welcomePromptShowTimerId = null;
            this.tryShowWelcomePromptBubble();
        }, WB_AI_CHAT_WELCOME_SHOW_DELAY_MS);
    }
    /**
     * Cancel the delayed welcome bubble display.
     */
    clearWelcomePromptShowTimer() {
        if (this.welcomePromptShowTimerId !== null) {
            window.clearTimeout(this.welcomePromptShowTimerId);
            this.welcomePromptShowTimerId = null;
        }
    }
    /**
     * True when the visitor previously dismissed the bubble via its close control.
     */
    isWelcomePromptDismissedInStorage() {
        try {
            return window.localStorage.getItem(WB_AI_CHAT_WELCOME_DISMISS_STORAGE_KEY) === '1';
        }
        catch {
            return false;
        }
    }
    /**
     * Persist that the welcome bubble should not be shown again (close control only).
     */
    persistWelcomePromptDismissed() {
        try {
            window.localStorage.setItem(WB_AI_CHAT_WELCOME_DISMISS_STORAGE_KEY, '1');
        }
        catch {
            /* Storage may be unavailable in private mode or blocked. */
        }
    }
    /**
     * Show the welcome bubble if allowed; builds the DOM on first use.
     */
    tryShowWelcomePromptBubble() {
        const prompt = wbAiChatConfig.welcomePrompt;
        if (!prompt || typeof prompt !== 'string' || this.isFullPage) {
            return;
        }
        if (this.isWelcomePromptDismissedInStorage()) {
            return;
        }
        if (this.isOpen) {
            return;
        }
        if (!this.toggleStack || !this.toggleButton) {
            return;
        }
        let bubble = this.welcomePromptBubble;
        if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'wb-ai-chat-welcome-prompt-bubble wb-ai-chat-welcome-prompt-bubble--hidden';
            bubble.setAttribute('role', 'status');
            const dismiss = document.createElement('button');
            dismiss.type = 'button';
            dismiss.className = 'wb-ai-chat-welcome-prompt-dismiss';
            dismiss.setAttribute('aria-label', 'Dismiss');
            const body = document.createElement('div');
            body.className = 'wb-ai-chat-welcome-prompt-text';
            body.textContent = prompt;
            dismiss.textContent = '\u00D7';
            bubble.appendChild(dismiss);
            bubble.appendChild(body);
            this.toggleStack.insertBefore(bubble, this.toggleButton);
            this.welcomePromptBubble = bubble;
            this.attachWelcomePromptDismissListenerIfNeeded();
        }
        bubble.classList.remove('wb-ai-chat-welcome-prompt-bubble--hidden');
    }
    /**
     * Hide the welcome bubble when the chat opens (does not set localStorage).
     */
    dismissWelcomePromptForChatOpen() {
        this.clearWelcomePromptShowTimer();
        if (this.welcomePromptBubble) {
            this.welcomePromptBubble.classList.add('wb-ai-chat-welcome-prompt-bubble--hidden');
        }
    }
    /**
     * Hide the welcome bubble and persist dismissal (close control).
     */
    dismissWelcomePromptFromCloseControl() {
        this.clearWelcomePromptShowTimer();
        this.persistWelcomePromptDismissed();
        if (this.welcomePromptBubble) {
            this.welcomePromptBubble.classList.add('wb-ai-chat-welcome-prompt-bubble--hidden');
        }
    }
    /**
     * Wire the welcome bubble dismiss button if present and not already bound.
     */
    attachWelcomePromptDismissListenerIfNeeded() {
        if (!this.welcomePromptBubble || this.eventListeners.welcomePromptDismissClick) {
            return;
        }
        const dismissBtn = this.welcomePromptBubble.querySelector('.wb-ai-chat-welcome-prompt-dismiss');
        if (!dismissBtn) {
            return;
        }
        this.eventListeners.welcomePromptDismissClick = (e) => {
            e.stopPropagation();
            this.dismissWelcomePromptFromCloseControl();
        };
        dismissBtn.addEventListener('click', this.eventListeners.welcomePromptDismissClick);
    }
    /**
     * Remove the welcome bubble dismiss listener (e.g. before rebinding after closeChat).
     */
    detachWelcomePromptDismissListener() {
        if (!this.welcomePromptBubble || !this.eventListeners.welcomePromptDismissClick) {
            this.eventListeners.welcomePromptDismissClick = null;
            return;
        }
        const dismissBtn = this.welcomePromptBubble.querySelector('.wb-ai-chat-welcome-prompt-dismiss');
        if (dismissBtn) {
            dismissBtn.removeEventListener('click', this.eventListeners.welcomePromptDismissClick);
        }
        this.eventListeners.welcomePromptDismissClick = null;
    }
    showLoadingState() {
        if (this.isFullPage) {
            const shortcodeContainer = document.getElementById('wb-ai-chat-full-page__container');
            if (shortcodeContainer) {
                shortcodeContainer.innerHTML =
                    `<div class="wb-ai-chat-full-page-loading"><div class="wb-ai-chat-spinner"></div><p>${wbAiChatConfig.strings.connecting}</p></div>`;
            }
        }
        else {
            if (!this.toggleButton) {
                this.toggleButton = document.createElement('button');
                this.toggleButton.id = 'wb-ai-chat-toggle';
                this.toggleButton.className = 'wb-ai-chat-toggle wb-ai-chat-loading';
                this.toggleButton.setAttribute('aria-label', wbAiChatConfig.strings.connecting);
                this.toggleButton.innerHTML = `
                    <div class="wb-ai-chat-spinner"></div>
                `;
                document.body.appendChild(this.toggleButton);
            }
            else {
                this.toggleButton.classList.add('wb-ai-chat-loading');
                this.toggleButton.innerHTML = `
                    <div class="wb-ai-chat-spinner"></div>
                `;
            }
        }
    }
    hideLoadingState() {
        if (this.isFullPage) {
            const loadingElement = document.querySelector('.wb-ai-chat-full-page-loading');
            if (loadingElement) {
                loadingElement.remove();
            }
        }
        else {
            if (this.toggleButton) {
                this.toggleButton.classList.remove('wb-ai-chat-loading');
                this.toggleButton.innerHTML = TOGGLE_ICON_SVG;
            }
        }
    }
    showToggleError() {
        if (this.toggleButton) {
            this.toggleButton.classList.add('wb-ai-chat-error');
            this.toggleButton.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;
            this.toggleButton.setAttribute('aria-label', 'AI Chat unavailable');
            this.toggleButton.disabled = true;
        }
    }
    setInputDisabled(disabled) {
        this.input.disabled = disabled;
    }
    showSendButton() {
        this.sendButton.style.display = 'block';
        this.stopButton.style.display = 'none';
    }
    showStopButton() {
        this.sendButton.style.display = 'none';
        this.stopButton.style.display = 'block';
    }
    debugShowTyping() {
        console.log('🔧 Debug: Showing typing indicator');
        this.showTypingIndicator();
    }
    debugHideTyping() {
        console.log('🔧 Debug: Hiding typing indicator');
        this.hideTypingIndicator();
    }
    debugClearChat() {
        console.log('🔧 Debug: Clearing chat');
        this.clearMessages();
        this.hideTypingIndicator();
    }
    debugAddUserMessage(text = 'Test user message') {
        console.log('🔧 Debug: Adding user message:', text);
        this.addMessage(text, 'user');
    }
    debugAddAssistantMessage(text = 'Test assistant message') {
        console.log('🔧 Debug: Adding assistant message:', text);
        this.addMessage(text, 'assistant');
    }
    debugGetState() {
        console.log('🔧 Debug: Current chat state', {
            isOpen: this.isOpen,
            isInitialized: this.isInitialized,
            currentConversationId: this.sessionManager.getCurrentConversationId(),
            hasTypingIndicator: !!this.typingIndicator,
            isStreaming: this.isStreaming,
            currentStatusPhase: this.currentStatusPhase,
            pendingUiAction: this.pendingUi?.action ?? null,
            messageCount: this.messagesContainer ? this.messagesContainer.querySelectorAll('.wb-ai-chat-message').length : 0,
        });
    }
}
// Initialize when DOM is ready. The script may run after DOMContentLoaded has already fired
// (async/delayed loading by performance plugins), so start immediately in that case.
const start = () => {
    const chatInterface = new WBAIChatInterface();
    // Console helpers are only exposed when WB_AI_CHAT_DEBUG is enabled in wp-config.php.
    if (!wbAiChatConfig.debug) {
        return;
    }
    window.wbAIChatDebug = {
        showTyping: () => chatInterface.debugShowTyping(),
        hideTyping: () => chatInterface.debugHideTyping(),
        clearChat: () => chatInterface.debugClearChat(),
        addUserMessage: (text) => chatInterface.debugAddUserMessage(text),
        addAssistantMessage: (text) => chatInterface.debugAddAssistantMessage(text),
        getState: () => chatInterface.debugGetState(),
        instance: chatInterface,
    };
};
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
}
else {
    start();
}
